if (!customElements.get('collection-variant-display')) {
  class CollectionVariantDisplay extends HTMLElement {
    constructor() {
      super();

      this.mobileBreakpoint = window.matchMedia('(max-width: 989px)');
      this.reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      );

      this.intersectingCards = new Set();

      this.handleMouseEnter = this.handleMouseEnter.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handleBreakpointChange =
        this.handleBreakpointChange.bind(this);
      this.handleIntersections =
        this.handleIntersections.bind(this);
      this.handleVariantNavClick =
        this.handleVariantNavClick.bind(this);
      this.handleBuilderTriggerClick =
        this.handleBuilderTriggerClick.bind(this);
    }

    connectedCallback() {
      this.allCards = Array.from(
        this.querySelectorAll(
          '[data-variant-card][data-card-key]'
        )
      );

      this.animatedCards = this.allCards.filter((card) =>
        card.classList.contains(
          'collection-variant-display__card--has-animation'
        )
      );

      this.cardsByKey = new Map(
        this.allCards.map((card) => [
          card.dataset.cardKey,
          card
        ])
      );

      this.variantNavButtons = Array.from(
        this.querySelectorAll(
          '[data-variant-nav-button][data-target-card-key]'
        )
      );

      this.builderTriggers = Array.from(
        this.querySelectorAll(
          '[data-kb-builder-trigger]'
        )
      );

      this.variantSwatches = this.querySelector(
        '.collection-variant-display__variant-swatches'
      );

      this.animatedCards.forEach((card) => {
        card.addEventListener(
          'mouseenter',
          this.handleMouseEnter
        );

        card.addEventListener(
          'mouseleave',
          this.handleMouseLeave
        );

        card.addEventListener(
          'focusin',
          this.handleFocusIn
        );

        card.addEventListener(
          'focusout',
          this.handleFocusOut
        );
      });

      this.variantNavButtons.forEach((button) => {
        button.addEventListener(
          'click',
          this.handleVariantNavClick
        );
      });

      this.builderTriggers.forEach((trigger) => {
        trigger.addEventListener(
          'click',
          this.handleBuilderTriggerClick
        );
      });

      this.mobileBreakpoint.addEventListener(
        'change',
        this.handleBreakpointChange
      );

      this.updateInteractionMode();
    }

    disconnectedCallback() {
      this.animatedCards?.forEach((card) => {
        card.removeEventListener(
          'mouseenter',
          this.handleMouseEnter
        );

        card.removeEventListener(
          'mouseleave',
          this.handleMouseLeave
        );

        card.removeEventListener(
          'focusin',
          this.handleFocusIn
        );

        card.removeEventListener(
          'focusout',
          this.handleFocusOut
        );
      });

      this.variantNavButtons?.forEach((button) => {
        button.removeEventListener(
          'click',
          this.handleVariantNavClick
        );
      });

      this.builderTriggers?.forEach((trigger) => {
        trigger.removeEventListener(
          'click',
          this.handleBuilderTriggerClick
        );
      });

      this.mobileBreakpoint.removeEventListener(
        'change',
        this.handleBreakpointChange
      );

      this.disconnectObserver();
    }

    updateInteractionMode() {
      this.clearActiveCards();
      this.intersectingCards.clear();

      if (this.mobileBreakpoint.matches) {
        this.createObserver();
        return;
      }

      this.disconnectObserver();
    }

    createObserver() {
      this.disconnectObserver();

      if (!this.allCards.length) {
        return;
      }

      this.observer = new IntersectionObserver(
        this.handleIntersections,
        {
          root: null,
          rootMargin: '-45% 0px -45% 0px',
          threshold: 0
        }
      );

      this.allCards.forEach((card) => {
        this.observer.observe(card);
      });
    }

    disconnectObserver() {
      if (!this.observer) {
        return;
      }

      this.observer.disconnect();
      this.observer = null;
      this.intersectingCards.clear();
    }

    handleIntersections(entries) {
      if (!this.mobileBreakpoint.matches) {
        return;
      }

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.intersectingCards.add(entry.target);
        } else {
          this.intersectingCards.delete(entry.target);
        }
      });

      if (!this.intersectingCards.size) {
        return;
      }

      const activeCard = Array.from(
        this.intersectingCards
      ).sort((firstCard, secondCard) => {
        const firstRect = firstCard.getBoundingClientRect();
        const secondRect = secondCard.getBoundingClientRect();

        const firstDistance = Math.abs(
          firstRect.top +
            firstRect.height / 2 -
            window.innerHeight / 2
        );

        const secondDistance = Math.abs(
          secondRect.top +
            secondRect.height / 2 -
            window.innerHeight / 2
        );

        return firstDistance - secondDistance;
      })[0];

      this.setActiveCard(activeCard);

      const cardKey = activeCard.dataset.cardKey;

      if (cardKey) {
        this.setActiveNavButton(cardKey);
      }
    }

    handleVariantNavClick(event) {
      const button = event.currentTarget;
      const cardKey = button.dataset.targetCardKey;

      if (!cardKey) {
        return;
      }

      const targetCard = this.cardsByKey.get(cardKey);

      if (!targetCard) {
        return;
      }

      this.setActiveCard(targetCard);
      this.setActiveNavButton(cardKey);

      targetCard.scrollIntoView({
        behavior: this.reducedMotion.matches
          ? 'auto'
          : 'smooth',
        block: 'center'
      });
    }

    handleBuilderTriggerClick() {
      document.dispatchEvent(
        new CustomEvent('kb:builder:open', {
          bubbles: true,
          composed: true,
          detail: {
            source: 'collection-variant-display'
          }
        })
      );
    }

    handleBreakpointChange() {
      this.updateInteractionMode();
    }

    handleMouseEnter(event) {
      if (this.mobileBreakpoint.matches) {
        return;
      }

      this.setActiveCard(event.currentTarget);
    }

    handleMouseLeave(event) {
      if (this.mobileBreakpoint.matches) {
        return;
      }

      event.currentTarget.classList.remove('is-active');
    }

    handleFocusIn(event) {
      if (this.mobileBreakpoint.matches) {
        return;
      }

      this.setActiveCard(event.currentTarget);
    }

    handleFocusOut(event) {
      const card = event.currentTarget;

      if (card.contains(event.relatedTarget)) {
        return;
      }

      if (!this.mobileBreakpoint.matches) {
        card.classList.remove('is-active');
      }
    }

    setActiveCard(activeCard) {
      this.animatedCards.forEach((card) => {
        card.classList.toggle(
          'is-active',
          card === activeCard
        );
      });
    }

    setActiveNavButton(cardKey) {
      let activeButton = null;

      this.variantNavButtons.forEach((button) => {
        const isActive =
          button.dataset.targetCardKey === cardKey;

        button.classList.toggle(
          'collection-variant-display__variant-swatch--active',
          isActive
        );

        button.setAttribute(
          'aria-pressed',
          isActive ? 'true' : 'false'
        );

        if (isActive) {
          activeButton = button;
        }
      });

      if (activeButton) {
        this.scrollVariantButtonIntoView(activeButton);
      }
    }

    scrollVariantButtonIntoView(button) {
      if (!this.variantSwatches) {
        return;
      }

      const containerRect =
        this.variantSwatches.getBoundingClientRect();

      const buttonRect = button.getBoundingClientRect();

      const buttonCenter =
        buttonRect.left -
        containerRect.left +
        this.variantSwatches.scrollLeft +
        buttonRect.width / 2;

      const targetScrollLeft =
        buttonCenter -
        this.variantSwatches.clientWidth / 2;

      this.variantSwatches.scrollTo({
        left: targetScrollLeft,
        behavior: this.reducedMotion.matches
          ? 'auto'
          : 'smooth'
      });
    }

    clearActiveCards() {
      this.animatedCards.forEach((card) => {
        card.classList.remove('is-active');
      });
    }
  }

  customElements.define(
    'collection-variant-display',
    CollectionVariantDisplay
  );
}